import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../api';
import { escapeHtml } from '../utils/escapeHtml';
import ExpensePayments from '../components/ExpensePayments';
import ReceiptLedger from '../components/ReceiptLedger';
import ComplianceIndicator from '../components/ComplianceIndicator';
import ExpenseForm, { ExpenseData } from '../components/ExpenseForm';

interface Document {
    id: string;
    document_type: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    uploaded_at: string;
    is_primary: number;
}

interface Expense {
    id: string;
    date_of_service: string;
    provider: string;
    patient_name: string;
    amount: number;
    category: string;
    family_member: string;
    tax_year: number;
    reimbursement_status: string;
    hsa_eligible: number;
    eligibility_confidence: number;
    eligibility_reasoning: string;
    irs_compliance_status: 'green' | 'yellow' | 'red';
    created_by_name?: string;
    created_at: string;
    documents: Document[];
    items?: Array<{ id: string; description: string; total: number; eligibility_status: string }>;
}

const DOC_TYPES = [
    { value: 'receipt', label: 'Receipt' },
    { value: 'eob', label: 'Explanation of Benefits' },
    { value: 'payment_proof', label: 'Payment Proof' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'other', label: 'Other' },
];

export default function ExpenseDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const api = useApi();

    const [expense, setExpense] = useState<Expense | null>(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadType, setUploadType] = useState('receipt');

    useEffect(() => {
        if (id) loadExpense();
    }, [id]);

    async function loadExpense() {
        try {
            const result = await api.getExpense(id!);
            if (result.success) {
                setExpense(result.data as Expense);
            }
        } catch (error) {
            console.error('Failed to load expense:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete() {
        if (!confirm('Are you sure you want to delete this expense? This cannot be undone.')) return;

        try {
            await api.deleteExpense(id!);
            navigate('/expenses');
        } catch (error) {
            console.error('Failed to delete expense:', error);
            alert('Failed to delete expense');
        }
    }

    async function handleDocumentUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const result = await api.uploadDocument(id!, file, uploadType);
            if (!result.success) throw new Error(result.error || 'Document was not stored');
            await loadExpense();
        } catch (error) {
            console.error('Failed to upload document:', error);
            alert('Failed to upload document');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    }

    async function downloadOriginal(doc: Document) {
        try {
            const blob = await api.getDocument(doc.id);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.file_name;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            alert('Unable to download the original. Please retry.');
        }
    }

    async function handleDeleteDocument(docId: string) {
        if (!confirm('Delete this document?')) return;

        try {
            await api.deleteDocument(docId);
            await loadExpense();
        } catch (error) {
            console.error('Failed to delete document:', error);
        }
    }

    async function handleSetPrimary(docId: string) {
        try {
            await api.setPrimaryDocument(docId);
            await loadExpense();
        } catch (error) {
            console.error('Failed to set primary document:', error);
        }
    }

    function printReport() {
        if (!expense) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to print the report.');
            return;
        }

        const complianceLabel = expense.irs_compliance_status === 'green' 
            ? '🟢 Documents Present — Review Required'
            : expense.irs_compliance_status === 'yellow' 
                ? '🟡 Partial Documentation' 
                : '🔴 Needs Documentation';

        const docsList = expense.documents.map(doc => `
            <div style="margin-top: 20px; page-break-inside: avoid;">
                <h4 style="margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px;">
                    ${escapeHtml(doc.document_type.toUpperCase())}: ${escapeHtml(doc.file_name)}
                </h4>
                <p style="color: #6b7280; font-size: 12px;">
                    Uploaded: ${new Date(doc.uploaded_at).toLocaleDateString()}
                    ${doc.is_primary ? ' • Primary Document' : ''}
                </p>
            </div>
        `).join('');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>HSA Expense Report - ${escapeHtml(expense.provider || 'Expense')}</title>
                    <style>
                        * { box-sizing: border-box; margin: 0; padding: 0; }
                        body { 
                            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
                            padding: 40px; 
                            color: #1f2937; 
                            max-width: 800px; 
                            margin: 0 auto;
                            line-height: 1.6;
                        }
                        .header {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            border-bottom: 3px solid #10b981;
                            padding-bottom: 20px;
                            margin-bottom: 30px;
                        }
                        .logo {
                            display: flex;
                            align-items: center;
                            gap: 12px;
                        }
                        .logo-icon {
                            width: 48px;
                            height: 48px;
                            background: #10b981;
                            border-radius: 10px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: white;
                            font-size: 24px;
                        }
                        .logo-text {
                            font-size: 24px;
                            font-weight: 700;
                        }
                        .logo-subtitle {
                            font-size: 12px;
                            color: #6b7280;
                        }
                        .amount-box {
                            text-align: right;
                        }
                        .amount {
                            font-size: 36px;
                            font-weight: 700;
                            color: #10b981;
                        }
                        .compliance-badge {
                            display: inline-block;
                            padding: 4px 12px;
                            border-radius: 20px;
                            font-size: 12px;
                            font-weight: 600;
                            margin-top: 8px;
                        }
                        .compliance-green { background: #d1fae5; color: #065f46; }
                        .compliance-yellow { background: #fef3c7; color: #92400e; }
                        .compliance-red { background: #fee2e2; color: #991b1b; }
                        .section {
                            margin-bottom: 30px;
                        }
                        .section-title {
                            font-size: 14px;
                            font-weight: 600;
                            color: #6b7280;
                            text-transform: uppercase;
                            letter-spacing: 0.05em;
                            margin-bottom: 15px;
                            border-bottom: 1px solid #e5e7eb;
                            padding-bottom: 8px;
                        }
                        .details-grid {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 15px 30px;
                        }
                        .detail-item {
                            display: flex;
                            flex-direction: column;
                        }
                        .detail-label {
                            font-size: 12px;
                            color: #6b7280;
                            margin-bottom: 2px;
                        }
                        .detail-value {
                            font-weight: 500;
                        }
                        .footer {
                            margin-top: 40px;
                            padding-top: 20px;
                            border-top: 1px solid #e5e7eb;
                            font-size: 11px;
                            color: #9ca3af;
                            text-align: center;
                        }
                        @media print {
                            body { padding: 20px; }
                            .no-print { display: none; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="logo">
                            <div class="logo-icon">🛡️</div>
                            <div>
                                <div class="logo-text">HSA Expense Report</div>
                                <div class="logo-subtitle">Expense Documentation — Financial Review Required</div>
                            </div>
                        </div>
                        <div class="amount-box">
                            <div class="amount">$${expense.amount.toFixed(2)}</div>
                            <div class="compliance-badge compliance-${escapeHtml(expense.irs_compliance_status)}">
                                ${complianceLabel}
                            </div>
                        </div>
                    </div>

                    <div class="section">
                        <div class="section-title">Expense Details</div>
                        <div class="details-grid">
                            <div class="detail-item">
                                <span class="detail-label">Provider / Merchant</span>
                                <span class="detail-value">${escapeHtml(expense.provider || 'Not specified')}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Date of Service</span>
                                <span class="detail-value">${escapeHtml(expense.date_of_service)}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Patient Name</span>
                                <span class="detail-value">${escapeHtml(expense.patient_name || 'Self')}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Family Member</span>
                                <span class="detail-value">${escapeHtml(expense.family_member || 'Self')}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Category</span>
                                <span class="detail-value">${escapeHtml(expense.category || 'Not categorized')}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Tax Year</span>
                                <span class="detail-value">${expense.tax_year}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Reimbursement Status</span>
                                <span class="detail-value">${escapeHtml(expense.reimbursement_status)}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Legacy Eligibility Flag</span>
                                <span class="detail-value">${expense.hsa_eligible ? 'Legacy flag set — ledger review required' : 'Review required / not approved'}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Expense ID</span>
                                <span class="detail-value">${escapeHtml(expense.id)}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Created</span>
                                <span class="detail-value">${new Date(expense.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>

                    <div class="section">
                        <div class="section-title">Attached Documentation (${expense.documents.length} files)</div>
                        ${expense.documents.length > 0 ? docsList : '<p style="color: #6b7280;">No documents attached.</p>'}
                    </div>

                    <div class="footer">
                        <p>Generated on ${new Date().toLocaleString()} • HSA Tracker — Receipt Records</p>
                        <p>This report is for record-keeping purposes. Please retain original documents for IRS compliance.</p>
                    </div>

                    <script>
                        window.onload = function() { window.print(); }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    }

    if (loading) {
        return <div className="page-loading"><div className="spinner"></div></div>;
    }

    if (!expense) {
        return <div className="error-page">Expense not found</div>;
    }

    return (
        <div className="expense-detail-page">
            <header className="page-header">
                <div className="header-back">
                    <Link to="/expenses" className="back-link">← Back to Expenses</Link>
                </div>
                <div className="header-actions">
                    <button onClick={printReport} className="btn btn-secondary print-btn">
                        🖨️ Print Report
                    </button>
                    <button onClick={() => setEditing(!editing)} className="btn btn-secondary">
                        {editing ? 'Cancel' : '✏️ Edit'}
                    </button>
                    <button onClick={handleDelete} className="btn btn-danger">🗑️ Delete</button>
                </div>
            </header>

            <div className="expense-detail-grid">
                {/* Main Info Card */}
                <div className="card expense-info-card">
                    <div className="expense-header">
                        <ComplianceIndicator status={expense.irs_compliance_status} showLabel />
                        <h1>{expense.provider || 'Expense Details'}</h1>
                    </div>

                    {editing ? (
                        <ExpenseForm
                            expense={expense}
                            onSave={async (data: ExpenseData) => {
                                await api.updateExpense(id!, data as unknown as Record<string, unknown>);
                                await loadExpense();
                                setEditing(false);
                            }}
                            onCancel={() => setEditing(false)}
                        />
                    ) : (
                        <div className="expense-details">
                            <dl>
                                <dt>Date of Service</dt>
                                <dd>{expense.date_of_service}</dd>

                                <dt>Amount</dt>
                                <dd className="amount">${expense.amount.toFixed(2)}</dd>

                                <dt>Provider/Merchant</dt>
                                <dd>{expense.provider || '-'}</dd>

                                <dt>Patient Name</dt>
                                <dd>{expense.patient_name || '-'}</dd>

                                <dt>Category</dt>
                                <dd>
                                    <span className={`category-badge category-${expense.category || 'none'}`}>
                                        {expense.category || 'Uncategorized'}
                                    </span>
                                </dd>

                                <dt>Family Member</dt>
                                <dd>{expense.family_member || '-'}</dd>

                                <dt>Tax Year</dt>
                                <dd>{expense.tax_year}</dd>

                                <dt>Legacy Reimbursement Status</dt>
                                <dd className={`status-${escapeHtml(expense.reimbursement_status)}`}>
                                    {expense.reimbursement_status}
                                </dd>

                                <dt>Legacy Eligibility Flag</dt>
                                <dd>{expense.hsa_eligible ? 'Legacy flag set — ledger review required' : 'Review required / not approved'}</dd>

                                <dt>Added By</dt>
                                <dd>{expense.created_by_name || 'Unknown'}</dd>

                                <dt>Created</dt>
                                <dd>{new Date(expense.created_at).toLocaleDateString()}</dd>
                            </dl>
                        </div>
                    )}
                </div>

                {expense.items && expense.items.length > 0 && <div className="card">
                    <h2>Item Review</h2>
                    <p>Review decisions do not calculate a reimbursable balance.</p>
                    <table><thead><tr><th>Item</th><th>Amount</th><th>Review</th></tr></thead>
                        <tbody>{expense.items.map(item => <tr key={item.id}>
                            <td>{item.description}</td><td>${item.total.toFixed(2)}</td>
                            <td>{item.eligibility_status.replace(/_/g, ' ')}</td>
                        </tr>)}</tbody>
                    </table>
                </div>}

                <ExpensePayments expenseId={expense.id} />
                <ReceiptLedger expenseId={expense.id} documents={expense.documents} />

                {/* Documents Card */}
                <div className="card documents-card">
                    <h2>📎 Originals stored with this expense</h2>

                    {/* Upload Section */}
                    <div className="document-upload">
                        <select
                            value={uploadType}
                            onChange={(e) => setUploadType(e.target.value)}
                            aria-label="Document type"
                        >
                            {DOC_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                        <label className="upload-btn">
                            {uploading ? '⏳ Uploading...' : '➕ Add Document'}
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={handleDocumentUpload}
                                disabled={uploading}
                                hidden
                            />
                        </label>
                    </div>

                    {/* Document List */}
                    {expense.documents.length === 0 ? (
                        <p className="no-documents">No original is anchored here. Shared evidence is listed in Linked source evidence above.</p>
                    ) : (
                        <ul className="document-list">
                            {expense.documents.map((doc) => (
                                <li key={doc.id} className={`document-item ${doc.is_primary ? 'primary' : ''}`}>
                                    <div className="doc-info">
                                        <span className="doc-type">{doc.document_type}</span>
                                        <span className="doc-name">{doc.file_name}</span>
                                        <span className="doc-size">{(doc.file_size / 1024).toFixed(1)} KB</span>
                                        {doc.is_primary ? <span className="primary-badge">Primary</span> : null}
                                    </div>
                                    <div className="doc-actions">
                                        <button onClick={() => downloadOriginal(doc)} className="btn btn-sm">
                                            Download Original
                                        </button>
                                        {!doc.is_primary && (
                                            <button
                                                onClick={() => handleSetPrimary(doc.id)}
                                                className="btn btn-sm btn-secondary"
                                            >
                                                ⭐ Set Primary
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDeleteDocument(doc.id)}
                                            className="btn btn-sm btn-danger"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Compliance Help */}
                    {expense.irs_compliance_status !== 'green' && (
                        <div className="compliance-help">
                            <h3>💡 Document presence only — financial review is separate:</h3>
                            {expense.irs_compliance_status === 'red' && (
                                <p>Upload a receipt, invoice, or EOB to move to 🟡 Yellow status.</p>
                            )}
                            {expense.irs_compliance_status === 'yellow' && (
                                <p>Upload payment proof (credit card statement) to reach 🟢 Green status.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
